/**
 * mrmsRadarService.ts — بيان أرشيف رادار MRMS (خمسة نطاقات).
 *
 * يقرأ `radar.json` الذي يكتبه `backend/scripts/mrms_to_raster.py` بجوار الصور.
 * البيان ضروري لا زائد لثلاثة أسباب:
 *
 *  ❶ **الحدود** تأتي من الشبكة المصدر نفسها لا من رقم مكتوب يدوياً في العميل — وهذا
 *    ما جعل إضافة أربعة نطاقات (ألاسكا، الكاريبي، هاواي، غوام) بلا سطر واحد من
 *    الإحداثيات هنا: كلٌّ منها يحمل حدوده معه.
 *  ❷ **قائمة الإطارات** هي مصدر الحركة: أسماء الملفّات تحمل وقت الرصد، فلا يعرف
 *    العميل أيّها موجود إلا منها.
 *  ❸ **قائمة النطاقات** تحدّد كم مصدر صورة تُنشئ الطبقة — فيكفي أن يضيف المولّد
 *    نطاقاً سادساً ليظهر على الخريطة بلا تعديل في الواجهة.
 *
 * ولاحظ ما **لا** نحتاجه هنا: وسم كسر كاش. اسم كل إطار يحمل وقته فهو فريد وثابت
 * إلى الأبد بعد كتابته — فيُخزَّن في المتصفّح بلا تاريخ انتهاء، وهو شرط أن يعمل
 * التحريك بسلاسة بدل إعادة تنزيل نصف ميغابايت لكل خطوة.
 */

import { prefetchRasterBytes } from './rasterImageCache';
import type { MapBounds } from '../store/types';

export interface RadarBounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

/**
 * هل يلامس هذا النطاق ما تراه الخريطة الآن؟
 *
 * ── لماذا وُجدت (قياس 2026-09-19) ───────────────────────────────────────────
 * الطبقة كانت تُركّب **كل النطاقات دائماً**: أحد عشر نطاق MRMS وتسعة OPERA وكندا
 * وإيطاليا. فالزائر فوق كانساس يُنزّل إطارات أوروبا وإيطاليا في كل خطوة زمنية — رُصد
 * ذلك حرفياً في سجلّ الشبكة.
 *
 * والكلفة تظهر في التشغيل لا في الفتح: إطار MRMS الواحد **484 كيلوبايت**، فكل خطوة
 * على الشريط تطلب ~5 ميغابايت من الإطارات قبل أن تكتمل. ولذلك كانت الحلقة تزحف:
 * قِيست 12 دقيقة عرض في 40 ثانية ثم دقيقتان في مئة ثانية — وهو ما يصفه المستخدم
 * بأن «الرادار لا يتحرّك بسلاسة كبقيّة الطبقات».
 *
 * والهامش ربع درجة: نطاقٌ يلامس حافّة الشاشة يجب أن يكون جاهزاً قبل أن يدخلها
 * تماماً، وإلّا ظهر فراغٌ لحظةَ التحريك.
 */
export function domainInView(bounds: RadarBounds, view: MapBounds | null, margin = 0.25): boolean {
    if (!view) return true;                 // لا حدود معروفة بعد: لا نحجب شيئاً
    if (bounds.south > view.north + margin || bounds.north < view.south - margin) return false;
    // لفّ خط الطول: نافذةٌ تعبر ±180 تُمثَّل بـwest > east، وحينها يكفي ألّا ينفصل
    // النطاق عن **أحد** شقّيها.
    if (view.west > view.east) {
        return !(bounds.west > view.east + margin && bounds.east < view.west - margin);
    }
    return !(bounds.west > view.east + margin || bounds.east < view.west - margin);
}

export interface RadarFrame {
    /** وقت الرصد ISO — يُعرض للمستخدم. */
    time: string | null;
    /** إطار تنبّؤ (بعد آخر رصد) لا رصد — راجع radar_nowcast.py. */
    forecast?: boolean;
    /** اسم الملفّ داخل مجلّد الراسترات. */
    file: string;
    /**
     * نسخة مصغَّرة (عرض 1024) — تُعلَن فقط حين يكتبها المولّد فعلاً.
     *
     * الإطار الكامل 4096×4096 = 16.8 ميغابكسل، أي **67 ميغابايت بعد فكّ الترميز**
     * يُرفع نسيجاً في كل خطوة زمنية. وذلك — لا الشبكة — هو ما كان يُبقي الحلقة عند
     * تحديثين في الثانية. راجع SMALL_W في mrms_to_raster.py.
     */
    small?: string;
    /**
     * بادئة شجرة البلاطات لهذا الإطار (`radar-tiles/<نطاق>/<ختم>`) — تُعلَن فقط حين
     * يكتبها المولّد، كـ`small` تماماً.
     *
     * البلاطات تجعل الكلفة تتبع **الشاشة** لا القارّة: المصغَّرة خفّضت الميغابكسلات
     * ستّة عشر ضعفاً لكنها أبقت العيب الأصلي — التكبير على مدينة يُنزّل القارّة
     * كاملةً. هنا يُطلب ما يُرى وحده، بدقّته الكاملة، وبكلفةٍ ثابتة مهما بلغ التكبير.
     */
    tiles?: string;
}

/**
 * نطاق رصد واحد. MRMS ينشر خمسة نطاقات منفصلة (القارّة، ألاسكا، الكاريبي، هاواي،
 * غوام) لكلٍّ منها شبكته وحدوده ودقّته — فلكلٍّ صورته ومصدره على الخريطة.
 */
export interface RadarDomain {
    /** `conus` / `alaska` / `carib` / `hawaii` / `guam`. */
    id: string;
    /** للفسيفساء وحدها: ‏`tiles` في إطاراتها بلاطات قيم بهذا الترميز لا ألوان. */
    values?: { base: number; step: number };
    /** حدود الصورة بالدرجات — من الشبكة المصدر مباشرةً. */
    bounds: RadarBounds;
    /** من الأقدم إلى الأحدث. */
    frames: RadarFrame[];
    validTime: string | null;
    width: number;
    height: number;
    /**
     * اسم ملفّ قناع التغطية، أو null إن لم يُولَّد.
     *
     * القناع مشتقّ من علم `-999` في MRMS نفسه («خارج تغطية أي رادار»)، لا من تقدير
     * هندسي لقباب المحطّات. وهو ما يفصل «لا مطر هنا» عن «لا رادار هنا» — تمييزٌ
     * يقلب معنى المساحة الفارغة على الخريطة.
     */
    coverage: string | null;
}

export interface RadarManifest {
    /** `mercator`: الصور مُسقَطة سلفاً فيصحّ استيفاء MapLibre الخطّي عليها. */
    projection: string;
    domains: RadarDomain[];
    source: string;
}

const RADAR_DIR = 'rasters/';
/**
 * بيانٌ لكل مولّد، لا بيانٌ مشترك يكتبه اثنان:
 *   • `radar.json`       — NOAA MRMS: الولايات المتحدة وأقاليمها.
 *   • `radar-opera.json` — EUMETNET OPERA: أوروبا (CC BY 4.0).
 *   • `radar-canada.json` — ECCC: كندا خارج تغطية MRMS (رخصة ECCC، تجاري مسموح).
 *   • `radar-italy.json` — Radar-DPC: جنوب إيطاليا وصقلية خارج تغطية OPERA (CC BY-SA 4.0).
 *   • `radar-taiwan.json` — CWA O-A0059-001: تايوان ومضيقها (OGDL-Taiwan-1.0).
 * ملفٌّ مشترك كان سيعني كاتبَين يتسابقان عليه، فيقرأ المتصفّح أحياناً نصفاً من
 * هذا ونصفاً من ذاك. والدمج هنا بسيط لأن النطاقات مستقلّة أصلاً: لكلٍّ حدوده
 * وإطاراته، والمحور الزمني يختار لكل نطاق أحدث إطارٍ لا يتجاوز اللحظة المعروضة.
 */
const RADAR_MANIFESTS = ['radar.json', 'radar-opera.json', 'radar-canada.json', 'radar-italy.json', 'radar-taiwan.json'] as const;

/**
 * MRMS يُنتج دورة كل دقيقتين. نسأل كل دقيقتين ونصف: أقلّ من ذلك يهدر طلبات على
 * بيان لم يتغيّر، وأكثر منه يُظهر رادراً متأخّراً بلا داعٍ.
 */
export const RADAR_POLL_MS = 150_000;

/**
 * زمن عرض الإطار الواحد أثناء التحريك.
 *
 * ‏450 مللي ≈ إيقاع Zoom Earth للرادار: أسرع منه يجعل الحلقة تومض فلا تُقرأ حركة
 * العاصفة، وأبطأ منه يجعل الاثني عشر إطاراً تستغرق أكثر من ثمان ثوانٍ للدورة.
 */
export const RADAR_STEP_MS = 450;

/**
 * وقفة عند آخر إطار قبل العودة للبداية.
 *
 * بلا هذه الوقفة تعود الحلقة فوراً من الأحدث إلى الأقدم، فتبدو **قفزةً للخلف**
 * لا يميّزها المشاهد عن حركة حقيقية معاكسة — وهي أكثر ما يُربك في حلقات الرادار.
 */
export const RADAR_LOOP_HOLD_MS = 1200;

function base(): string {
    return `${import.meta.env.BASE_URL}${RADAR_DIR}`;
}

/** جذر الراسترات (بشرطةٍ في آخره) — لطبقة WebGL التي تبني روابط بلاطاتها بنفسها. */
export function radarRastersBase(): string {
    return base();
}

/** رابط ملفّ إطار بعينه. */
export function radarFrameUrl(frame: RadarFrame, preferSmall = false): string {
    // السقوط إلى الكامل حين لا مصغَّرة: أرشيفٌ سابق للترقية يبقى صالحاً، ولا يُطلب
    // ملفٌّ على الأمل فيردّ 404 ويُظهر الرادار فارغاً.
    const f = preferSmall && frame.small ? frame.small : frame.file;
    return `${base()}${f}`;
}

/**
 * رابط قناع التغطية.
 *
 * اسمه ثابت (لا يحمل وقتاً) لأن التغطية تتغيّر بعطل محطّة لا كل دورتين، والمولّد
 * يعيد كتابته كل ساعة. فنَسِمه بساعة التقريب: كسرُ كاشٍ مرّةً في الساعة بدل مرّة
 * كل دقيقتين، وبدل ألّا يُكسر أبداً فيعلَق القناع على عطلٍ انتهى.
 */
/**
 * قالب روابط بلاطات إطارٍ — لمصدر `raster` في MapLibre.
 *
 * البلاطة الشفّافة بالكامل لا يكتبها المولّد أصلاً (‏95.7% من الإطار بلا صدى)، فيردّ
 * الخادم 404 ويتجاوزها MapLibre بلا رسم — سلوكه الطبيعي لا حيلة، وهو ما يُسقط تلك
 * النسبة من الطلبات ومن التخزين معاً.
 */
export function radarTilesTemplate(prefix: string): string {
    return `${base()}${prefix}/{z}/{x}/{y}.webp`;
}

/**
 * مقاس بلاطة القيم **المعلَن** لـMapLibre (البلاطة نفسها 256).
 *
 * ‏raster-dem يمرّر كل بلاطة إلى عامل خلفي يقرأ بكسلاتها إلى بنية ارتفاع ثم يعيدها،
 * ويخيطها بحوافّ جاراتها الثماني — كلفةٌ لكل بكسل لا تدفعها بلاطات الألوان (تُرفع
 * إلى المعالج مباشرةً). قِيس في 2026-09-22: وسيط تهيئة الإطار 155 مللي مقابل 34،
 * والإطار التالي لا يجهز في دوره أبداً فلا يقع المزج. إعلانها 512 يجعل MapLibre
 * يطلب مستوىً أخشن بدرجة: ربع البلاطات وربع البكسلات. والتفاصيل المفقودة لا تُرى
 * كحوافّ لأن القيمة تُستوفى على المعالج قبل التلوين — وهذا ما وُجدت له القيم.
 * ‏`?vts=256` يُعيد المقاس الكامل للمقارنة.
 */
export const RADAR_VALUES_TILE_SIZE =
    typeof window !== 'undefined' && /[?&]vts=256\b/.test(window.location.search) ? 256 : 512;

/** بلاطات القيم (PNG رمادي) — راجع `merge_values` في radar_mosaic.py. */
export function radarValuesTemplate(prefix: string): string {
    return `${base()}${prefix}/{z}/{x}/{y}.png`;
}

/** يطابق `MAX_Z` في backend/scripts/radar_tiles.py. */
export const RADAR_MAX_TILE_Z = 7;

/**
 * قناع التغطية يقف عند الرابع، والصدى يمضي إلى السابع.
 *
 * القناع حدودُ ما يراه الرادار: مساحات واسعة بحوافّ ناعمة لا تفاصيل فيها،
 * فالتكبير الرابع يصفها كما يصفها السابع، ويمدّده MapLibre بلا أثرٍ مرئي.
 * والفارق في الكلفة ستّة عشر ضعفاً في عدد البلاطات — وهو ما قيس ثمنه في
 * 2026-09-20: قناعٌ بدقّة الصدى أنزل النبض من ٢٣ إلى ١٤.
 */
export const RADAR_COVERAGE_MAX_Z = 4;

/**
 * بلاطات إطارٍ التي **تلامس الشاشة** — قائمة الإحماء لا قائمة الأرشيف.
 *
 * ── لماذا وُجدت ──────────────────────────────────────────────────────────────
 * الإحماء كان يُنزّل المصغَّرة (1 ميغابكسل) لكل نطاق في كل إطار، وهي ما كان يُرسم.
 * وبعد التقطيع لم تعد تُرسَم، فصار الإحماء يُنزّل ما لا يُعرض: عشرون نطاقاً × ستّون
 * إطاراً ≈ عشرات الميغابايتات تُشبع الشبكة فتتضوّر ساعة التشغيل. قِيس ذلك في
 * 2026-09-20: الحلقة تتقدّم خطوة كل 2.1 ثانية بدل 450 مللي، والشريط بلا تلوين
 * إحماءٍ إطلاقاً لأن الجاهزية لا تكتمل أبداً.
 *
 * فنُحمّي ما سيُرسم فعلاً: البلاطات المتقاطعة مع الشاشة عند تكبيرها — حفنة ملفّات
 * بدل ميغابايتات، ويصير الشريط الأزرق صادقاً بدل أن يكون متعذّراً.
 *
 * والتكبير يُشتقّ من امتداد الشاشة لا من الخريطة: `z = log2(360 / عرض النافذة)` هي
 * نفس علاقة مِركاتور التي تبني بها MapLibre مستواها، فلا نحتاج إلى مرجع الخريطة هنا.
 */
export function radarViewTileUrls(domain: RadarDomain, frame: RadarFrame,
                                  view: MapBounds | null, mapZoom?: number, cap = 64): string[] {
    if (!frame.tiles) return [radarFrameUrl(frame, true)];   // نطاقٌ لم يُقطَّع بعد
    const b = domain.bounds;
    const west = Math.max(b.west, view ? view.west : -180);
    const east = Math.min(b.east, view ? view.east : 180);
    const north = Math.min(b.north, view ? view.north : 85);
    const south = Math.max(b.south, view ? view.south : -85);
    if (!(east > west) || !(north > south)) return [];       // النطاق خارج الشاشة

    /*
     * ── المستوى الذي سيرسمه MapLibre، لا تقديرٌ قريبٌ منه ──────────────────────
     *
     * كان المستوى يُقدَّر من عرض المنظور ثم يُهبَط به حتى لا تتجاوز البلاطات ثمانياً.
     * و MapLibre يرسم بلاطات 256 عند `floor(zoom) + 1` (عالمه 512 بكسلاً لا 256).
     * فكان الإحماء يُسخّن مستوًى إلى ثلاثة **أدنى** ممّا يُعرض — بلاطاتٍ لا تُرى أبداً —
     * وتُطلب الصحيحة لحظة ظهور الإطار، فيرسم MapLibre الأدنى ممدَّداً ريثما تصل.
     *
     * قيس في تتبّع 2026-09-21: 570 طلباً عند z=4 و397 عند z=3 مقابل 32 عند z=5
     * المعروض. وهو ما رآه المستخدم: «البلاطات مغبّشة عند الحركة» — كل خطوة تبدأ
     * مغبّشة ثم تتّضح. Zoom Earth لا يفعل ذلك لأن بلاطته جاهزة قبل دورها.
     *
     * والسقف صار أربعاً وستّين لا ثمانياً: شاشة حاسوبٍ كاملة عند المستوى الصحيح
     * نحو أربعين بلاطة، وكل واحدة بضع مئات من البايتات. الهبوط يبقى لما فوقه وحده.
     */
    const span = view ? Math.max(1e-6, view.east - view.west) : 360;
    // بلاطة 512 معلَنة تُطلب عند `floor(zoom)` لا `+1` — راجع RADAR_VALUES_TILE_SIZE.
    const zBoost = domain.values && RADAR_VALUES_TILE_SIZE === 512 ? 0 : 1;
    let z = Math.max(0, Math.min(RADAR_MAX_TILE_Z, mapZoom !== undefined
        ? Math.floor(mapZoom) + zBoost
        : Math.floor(Math.log2(360 / span))));

    const yOf = (lat: number, n: number) => {
        const s = Math.sin((Math.max(-85.05112878, Math.min(85.05112878, lat)) * Math.PI) / 180);
        return Math.floor((0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n);
    };
    // نهبط مستوًى كلّما تجاوز العدد السقف: الإحماء يجب أن يبقى حفنة، والمستوى الأخشن
    // يملأ الشاشة نفسها ببلاطاتٍ أقلّ ريثما تصل الدقيقة.
    for (; z >= 0; z--) {
        const n = 2 ** z;
        const x0 = Math.max(0, Math.floor(((west + 180) / 360) * n));
        const x1 = Math.min(n - 1, Math.floor(((east + 180) / 360) * n));
        const y0 = Math.max(0, yOf(north, n));
        const y1 = Math.min(n - 1, yOf(south, n));
        if ((x1 - x0 + 1) * (y1 - y0 + 1) <= cap || z === 0) {
            const out: string[] = [];
            for (let x = x0; x <= x1; x++) {
                // بلاطات القيم PNG: إحماء الـwebp في وضعها كان يسخّن ملفّاتٍ لا تُرسم،
                // فيُعلَن الإطار جاهزاً وهو لم يصل (nginx يردّ الغائب بلاطةً فارغة).
                const ext = domain.values ? 'png' : 'webp';
                for (let y = y0; y <= y1; y++) out.push(`${base()}${frame.tiles}/${z}/${x}/${y}.${ext}`);
            }
            return out;
        }
    }
    return [];
}

export function radarCoverageUrl(file: string): string {
    const hour = Math.floor(Date.now() / 3_600_000);
    return `${base()}${file}?h=${hour}`;
}

/**
 * يجلب البيان. يُعيد null عند أي فشل — الطبقة تختفي بهدوء بدل أن تكسر الخريطة،
 * وهي حالة متوقّعة تماماً في التطوير المحلّي حيث لا يعمل مولّد الراسترات.
 */
async function fetchOneManifest(file: string): Promise<RadarManifest | null> {
    try {
        const res = await fetch(`${base()}${file}`, { cache: 'no-cache' });
        if (!res.ok) return null;
        const data = (await res.json()) as RadarManifest;
        if (!Array.isArray(data?.domains) || data.domains.length === 0) return null;
        // نُسقط أي نطاق ناقص بدل ردّ البيان كلّه: عطبُ نطاق واحد لا يُطفئ الرادار
        // في بقيّة العالم — وهذا هو الفرق بين خريطة ناقصة وخريطة فارغة.
        const domains = data.domains.filter(
            (d) => d?.bounds && typeof d.bounds.north === 'number'
                && Array.isArray(d.frames) && d.frames.length > 0,
        );
        return domains.length ? { ...data, domains } : null;
    } catch {
        return null;
    }
}

/**
 * يجلب البيانين معاً ويدمج نطاقاتهما. فشل أحدهما لا يُطفئ الآخر: رادار أوروبا
 * يبقى إن تعثّر مولّد أمريكا، والعكس. ويُعيد null حين لا يصل شيء — الطبقة تختفي
 * بهدوء، وهي حالة متوقّعة في التطوير المحلّي حيث لا يعمل المولّدان.
 */
/**
 * بيان الفسيفساء: شجرة بلاطات **واحدة** لكل لحظة، مدموجٌ فيها كل المصادر.
 *
 * يُنتجه `backend/scripts/radar_mosaic.py`. وقياس 2026-09-20 على الخادم: ٢٤ لحظة،
 * ١٦٣ م.ب (مقابل ٣٧٢ للنطاقات المنفصلة)، و٥٠ ثانية للبناء الكامل — أي ثانيتان لكل
 * لحظة جديدة. والمكسب الحقيقي عند العميل: مصدرٌ واحد بدل عشرين.
 */
export interface RadarMosaic {
    grid_seconds: number;
    maxzoom: number;
    /**
     * بادئة بلاطات **قناع التغطية** — شجرة واحدة لكل المصادر، ختمها من آخر تعديل
     * على الأقنعة لا من الزمن. فما دام القناع كما هو بقي المسار ثابتاً وبقي
     * الكاش صالحاً؛ وتغيّره يعني مساراً جديداً يُسقط الكاش من تلقائه.
     */
    coverage?: string | null;
    /** ‏`values`: شجرة بلاطات القيم للّحظة إن بُنيت (راجع VALUE_* في radar_mosaic.py). */
    times: { time: string; prefix: string; values?: string }[];
    /** ترميز بلاطات القيم: ‏dBZ = R × step + base. */
    values?: { base: number; step: number };
}

/** بيان تنبّؤ الساعة القادمة — يكتبه backend/scripts/radar_nowcast.py. */
export interface RadarNowcast {
    base: string;
    step_seconds: number;
    values: { base: number; step: number };
    /** ‏`prefix`: بلاطات الألوان للوضع الافتراضي، و`values` لوضع القيم. */
    times: { time: string; values: string; prefix?: string }[];
    /**
     * حقل الحركة الذي قدّره المولّد (‏write_flow في radar_nowcast.py): نسيجٌ للعالم،
     * R/G إزاحة لكل `seconds` بوحدة عرض العالم مقسومةً على `scale`. تنزلق به العاصفة
     * بين إطارين كما في Windy بدل أن تذوب.
     */
    flow?: { file: string; scale: number; seconds: number };
}

export async function fetchRadarNowcast(): Promise<RadarNowcast | null> {
    try {
        const r = await fetch(`${base()}radar-nowcast.json?t=${Math.floor(Date.now() / 60000)}`,
            { cache: 'no-cache' });
        if (!r.ok) return null;
        const m = (await r.json()) as RadarNowcast;
        return Array.isArray(m?.times) ? m : null;
    } catch {
        return null;
    }
}

export async function fetchRadarMosaic(): Promise<RadarMosaic | null> {
    try {
        const r = await fetch(`${base()}radar-mosaic.json?t=${Math.floor(Date.now() / 60000)}`,
            { cache: 'no-cache' });
        if (!r.ok) return null;
        const m = await r.json() as RadarMosaic;
        // أقلّ من لحظتين لا يصنع حلقة — نبقى على المسار القديم بلا ضجيج.
        return m && Array.isArray(m.times) && m.times.length >= 2 ? m : null;
    } catch {
        return null;            // لم يُنشر بعد — المسار القديم يعمل كما هو
    }
}

export async function fetchRadarManifest(): Promise<RadarManifest | null> {
    const parts = (await Promise.all(RADAR_MANIFESTS.map(fetchOneManifest)))
        .filter((m): m is RadarManifest => m !== null);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0];
    const seen = new Set<string>();
    const domains = parts.flatMap((m) => m.domains).filter((d) => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return true;
    });
    return { ...parts[0], domains, source: parts.map((m) => m.source).join(' + ') };
}

/**
 * يُحمّي الإطارات في كاش المتصفّح مسبقاً — **ويُبلغ مؤشّر الشريط الزمني بما وصل**.
 *
 * بلا الإحماء يبدأ التحريك بتنزيل نصف ميغابايت عند كل خطوة، فتتقطّع الحلقة على أي
 * شبكة دون الممتازة ويبدو الرادار عالقاً.
 *
 * ── لماذا `prefetchRasterBytes` لا `new Image()` ─────────────────────────────
 *
 * كان الإحماء بـ`new Image()`، فيملأ كاش HTTP ولا يُخبر أحداً. والشريط الأزرق يسأل
 * سجلّ البايتات (`isRasterBytesReady`) الذي لا يكتب فيه إلا مسار الجلب هذا — فكان كل
 * إطار رادار «غير جاهز» إلى الأبد، والشريط فارغاً مهما اكتمل التنزيل. وهو ما رصده
 * المستخدم: الأزرق يظهر لكل الطبقات إلا الرادار.
 *
 * المسار نفسه الذي تسلكه نُسج التوقّعات يحلّ ذلك بلا سجلٍّ ثانٍ: يُسجَّل الرابط بعد
 * استهلاك الجسم كاملاً (لا قبله)، و404 يُحسم «مفقوداً» فلا يبقى رمادياً بلا نهاية.
 * والبايتات تذهب إلى نفس كاش HTTP الذي تطلب منه MapLibre الصورة لاحقاً.
 *
 * ‏التزامن محدود (أربعة) والإلغاء يوقف ما لم يبدأ: ‏fetch الجاري لا يُقطع، لكن تبديل
 * الطبقة لا يُطلق بعده طلباً جديداً.
 */
const PRELOAD_CONCURRENCY = 4;

// ‏preferSmall: الإحماء لا يعمل إلا أثناء التشغيل، والتشغيل يعرض المصغَّرة — فجلب
// الكاملة هنا كان يملأ الشبكة بملفّات لا تُرسم.
export function preloadRadarFrames(frames: readonly RadarFrame[], preferSmall = true): () => void {
    let stopped = false;
    let next = 0;
    const urls = frames.map((f) => radarFrameUrl(f, preferSmall));
    const pump = (): Promise<void> => {
        if (stopped || next >= urls.length) return Promise.resolve();
        const url = urls[next++];
        return prefetchRasterBytes(url).then(pump);
    };
    for (let i = 0; i < Math.min(PRELOAD_CONCURRENCY, urls.length); i++) void pump();
    return () => { stopped = true; };
}

/**
 * أركان الصورة بالترتيب الذي يطلبه مصدر `image` في MapLibre:
 * أعلى-يسار، أعلى-يمين، أسفل-يمين، أسفل-يسار.
 *
 * الترتيب ليس اصطلاحاً حرّاً: خطأ فيه لا يرمي استثناءً بل يقلب الصورة أو يعكسها،
 * فتظهر العواصف مرآةً لمواضعها الحقيقية — عطبٌ يصعب رصده على حقلٍ غير متماثل قليلاً.
 */
export function radarCoordinates(b: RadarBounds): [
    [number, number], [number, number], [number, number], [number, number],
] {
    return [
        [b.west, b.north],
        [b.east, b.north],
        [b.east, b.south],
        [b.west, b.south],
    ];
}
